import * as React from 'react';
import withStyles from 'react-jss';
import { CssType, ThemeType } from '../../theming/jssTypes';
import { WarningTwoTone } from '@ant-design/icons';
import { useTheme } from 'react-jss';
import Modal from 'src/design-system/components/atoms/Modal/Modal';
import FixedHeaderTable from 'src/design-system/components/molecules/FixedHeaderTable/FixedHeaderTable';
import Button from 'src/design-system/components/atoms/Button/Button';
import Tag from 'src/design-system/components/atoms/Tag/Tag';

const styles = (theme: ThemeType): CssType => ({
  partialPlanningModal: {
    '& .ant-modal-body': {
      padding: theme.spacing[0],
    },
    '& .ant-table-thead > tr > th': {
      textAlign: 'center',
      whiteSpace: 'nowrap',
    },
    '& .ant-table-tbody > tr > td': {
      textAlign: 'center',
    },
  },
  buttonRow: {
    padding: `${theme.spacing[3]} ${theme.spacing[4]}`,
    display: 'flex',
    justifyContent: 'flex-end',
    gap: theme.spacing[2],
    borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.border.soft}`,
  },
  buttonStyle: {
    minWidth: '100px',
  },
  warningIconStyle: {
    fontSize: theme.typography.fontSize.lg,
  },
  redDot: {
    width: theme.spacing[2],
    height: theme.spacing[2],
    borderRadius: theme.radius.full,
    backgroundColor: theme.color.semantic.brand.danger.primary,
    display: 'inline-block',
    marginRight: theme.spacing[2],
  },
  greenDot: {
    width: theme.spacing[2],
    height: theme.spacing[2],
    borderRadius: theme.radius.full,
    backgroundColor: theme.color.semantic.accent.green.strong,
    display: 'inline-block',
    marginRight: theme.spacing[2],
  },
  parentReferenceCell: {
    display: 'flex',
    alignItems: 'center',
  },
  messageCell: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.color.semantic.text.tertiary,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
  validMessageCell: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.color.semantic.accent.green.strong,
    wordBreak: 'break-word',
    whiteSpace: 'normal',
  },
  textCell: {
    textAlign: 'left',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    padding: theme.spacing[2],
  },
  modalBody: {
    padding: theme.spacing[4],
  },
  descriptionText: {
    fontSize: theme.typography.fontSize.sm,
    color: theme.color.semantic.text.tertiary,
    marginBottom: theme.spacing[4],
    lineHeight: theme.typography.lineHeight.normal,
  },
  excludedSection: {
    marginTop: theme.spacing[4],
    paddingTop: theme.spacing[4],
    borderTop: `${theme.border.width.sm} solid ${theme.color.semantic.border.soft}`,
  },
  excludedSectionTitle: {
    fontWeight: theme.typography.fontWeight.semibold,
    fontSize: theme.typography.fontSize.md,
    color: theme.color.semantic.text.secondary,
    marginBottom: theme.spacing[3],
  },
  excludedCNsContainer: {
    maxHeight: '200px',
    overflowY: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing[2],
    padding: theme.spacing[2],
    backgroundColor: theme.color.semantic.background.neutral.subtle,
    borderRadius: theme.radius.sm,
  },
  excludedCNTag: {
    margin: theme.spacing[0],
  },
  validSection: {
    marginTop: theme.spacing[4],
    paddingTop: theme.spacing[4],
    borderTop: `${theme.border.width.md} solid ${theme.color.semantic.accent.green.strong}`,
  },
  validSectionTitle: {
    fontWeight: theme.typography.fontWeight.bold,
    fontSize: theme.typography.fontSize.md,
    color: theme.color.semantic.accent.green.strong,
    marginBottom: theme.spacing[3],
  },
  consignmentsCell: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing[1],
    justifyContent: 'center',
  },
  consignmentTag: {
    margin: theme.spacing[0],
    fontSize: theme.typography.fontSize.xs,
  },
  tableCell: {
    textAlign: 'center',
    whiteSpace: 'nowrap',
  },
  table: {
    marginLeft: theme.spacing[0],
  },
});

interface IFailedValidation {
  parent_reference: string;
  reason: string;
  message: string;
  total_children: number;
  planned_children: number;
  unplanned_children: number;
}

interface IValidParentInfo {
  parent_reference: string;
  message: string;
  total_children: number;
  planned_children: number;
  unplanned_children: number;
  payload_references: string[];
  allow_child_cn_splitting: boolean;
}

interface IProps {
  classes?: any;
  visible: boolean;
  failedValidations: IFailedValidation[];
  excludedChildCNs?: string[];
  validParentInfo?: IValidParentInfo[];
  onBack: () => void;
  onProceedAnyway: () => void;
  hasAnyValidCNs: boolean;
}

const PartialPlanningModal = (props: IProps) => {
  const {
    classes,
    visible,
    failedValidations,
    excludedChildCNs = [],
    validParentInfo = [],
    onBack,
    onProceedAnyway,
    hasAnyValidCNs,
  } = props;

  const theme: any = useTheme();

  const columns = [
    {
      title: 'Parent CN',
      dataIndex: 'parent_reference',
      key: 'parent_reference',
      width: '20%',
      render: (text: string) => (
        <div className={classes.parentReferenceCell}>
          <span className={classes.redDot}></span>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Reason',
      dataIndex: 'message',
      key: 'message',
      width: '35%',
      align: 'left' as const,
      render: (text: string) => (
        <div className={classes.textCell}>
          <span className={classes.messageCell}>{text}</span>
        </div>
      ),
    },
    {
      title: 'Total Children',
      dataIndex: 'total_children',
      key: 'total_children',
      width: '12%',
      align: 'center' as const,
      className: classes.tableCell,
    },
    {
      title: 'Planned',
      dataIndex: 'planned_children',
      key: 'planned_children',
      width: '10%',
      align: 'center' as const,
      className: classes.tableCell,
    },
    {
      title: 'Unplanned',
      dataIndex: 'unplanned_children',
      key: 'unplanned_children',
      width: '10%',
      align: 'center' as const,
      className: classes.tableCell,
    },
  ];

  const validColumns = [
    {
      title: 'Parent CN',
      dataIndex: 'parent_reference',
      key: 'parent_reference',
      width: '15%',
      render: (text: string) => (
        <div className={classes.parentReferenceCell}>
          <span className={classes.greenDot}></span>
          <span>{text}</span>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'message',
      key: 'message',
      width: '30%',
      align: 'left' as const,
      render: (text: string) => (
        <div className={classes.textCell}>
          <span className={classes.validMessageCell}>{text}</span>
        </div>
      ),
    },
    {
      title: 'Total Children',
      dataIndex: 'total_children',
      key: 'total_children',
      width: '10%',
      align: 'center' as const,
      className: classes.tableCell,
    },
    {
      title: 'Planned',
      dataIndex: 'planned_children',
      key: 'planned_children',
      width: '8%',
      align: 'center' as const,
      className: classes.tableCell,
    },
    {
      title: 'To Be Planned',
      dataIndex: 'unplanned_children',
      key: 'unplanned_children',
      width: '12%',
      align: 'center' as const,
      className: classes.tableCell,
    },
    {
      title: 'Consignments',
      dataIndex: 'payload_references',
      key: 'payload_references',
      width: '25%',
      render: (payloadRefs: string[]) => {
        if (!payloadRefs || payloadRefs.length === 0) {
          return <span className={classes.tableCell}>-</span>;
        }
        const maxVisible = 3;
        const visibleRefs = payloadRefs.slice(0, maxVisible);
        const remainingCount = payloadRefs.length - maxVisible;
        return (
          <div className={classes.consignmentsCell}>
            {visibleRefs.map((ref: string, index: number) => (
              <Tag key={index} className={classes.consignmentTag} color="green">
                {ref}
              </Tag>
            ))}
            {remainingCount > 0 && (
              <Tag className={classes.consignmentTag} color="default">
                +{remainingCount} more
              </Tag>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <Modal
      title={
        <div>
          <WarningTwoTone
            twoToneColor={theme.color.semantic.brand.danger.primary}
            className={classes.warningIconStyle}
          />{' '}
          Partial Planning Not Allowed
        </div>
      }
      className={classes.partialPlanningModal}
      visible={visible}
      onCancel={null}
      destroyOnClose
      maskClosable={false}
      closable={false}
      width={1000}
      footer={null}
    >
      <div className={classes.modalBody}>
        <div className={classes.descriptionText}>
          The following consignments cannot be planned separately. If you proceed, these CNs will be excluded from trip planning.
        </div>
        
        <FixedHeaderTable
          pagination={false}
          scroll={{ y: 200 }}
          columns={columns}
          dataSource={failedValidations}
          rowKey={(record: IFailedValidation) => record.parent_reference}
          minHeight={200}
          className={classes.table}
        />
        
        {excludedChildCNs.length > 0 && (
          <div className={classes.excludedSection}>
            <div className={classes.excludedSectionTitle}>
              Consignments to be excluded:
            </div>
            <div className={classes.excludedCNsContainer}>
              {excludedChildCNs.map((cn: string, index: number) => (
                <Tag key={index} className={classes.excludedCNTag} color="blue">
                  {cn}
                </Tag>
              ))}
            </div>
          </div>
        )}

        {validParentInfo.length > 0 && (
          <div className={classes.validSection}>
            <div className={classes.validSectionTitle}>
              Consignments that will be planned:
            </div>
            <FixedHeaderTable
              pagination={false}
              scroll={{ y: 200 }}
              columns={validColumns}
              dataSource={validParentInfo}
              rowKey={(record: IValidParentInfo) => record.parent_reference}
              minHeight={200}
              className={classes.table}
            />
          </div>
        )}
      </div>
      <div className={classes.buttonRow}>
        <Button
          type="secondary"
          className={classes.buttonStyle}
          onClick={onBack}
        >
          Back
        </Button>
        <Button
          type="primary"
          className={classes.buttonStyle}
          onClick={onProceedAnyway}
          disabled={!hasAnyValidCNs}
        >
          Proceed Anyway
        </Button>
      </div>
    </Modal>
  );
};

export default withStyles(styles, { injectTheme: true })(PartialPlanningModal);
